import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { EnvironmentService } from '@indutrust/shared-env';
import type { CompanyTeamMember, TeamRole } from '@indutrust/shared-model';
import type { Observable } from 'rxjs';

/** Payload accepted by `POST /companies/{companyId}/team_members`. */
export type InviteCompanyTeamMemberPayload = {
  email: string;
  role: TeamRole;
};

/** Payload accepted by `PATCH /companies/{companyId}/team_members/{memberId}`. */
export type UpdateCompanyTeamMemberRolePayload = {
  role: TeamRole;
};

/**
 * API client for Company team-management endpoints under
 * `/companies/{companyId}/team_members`.
 *
 * Kept separate from {@link CompanyApiClient} because team members are a
 * nested sub-resource with a dynamic company id segment.
 *
 * Contract is frozen against the MSW handlers in `+mock/company-team.handlers.ts`
 * (GET list, POST invite, PATCH role, DELETE member; 403 + machine-readable
 * `cap-violation` / `last-owner` on R5 failures). OpenAPI regeneration is
 * pending until Postman exposes these endpoints.
 */
@Service()
export class CompanyTeamApiClient {
  private readonly envService = inject(EnvironmentService);
  private readonly http = inject(HttpClient);

  /**
   * Lists every team member for the given Company.
   * @param companyId - Target Company id.
   */
  getMembers(companyId: number): Observable<CompanyTeamMember[]> {
    return this.http.get<CompanyTeamMember[]>(this.teamMembersUrl(companyId));
  }

  /**
   * Invites a teammate by email with the requested sub-role.
   * @param companyId - Target Company id.
   * @param payload - Invite email and role.
   */
  invite(
    companyId: number,
    payload: InviteCompanyTeamMemberPayload
  ): Observable<CompanyTeamMember> {
    return this.http.post<CompanyTeamMember>(
      this.teamMembersUrl(companyId),
      payload
    );
  }

  /**
   * Removes a team member from the Company.
   * @param companyId - Target Company id.
   * @param memberId - Team membership row id.
   */
  remove(companyId: number, memberId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.teamMembersUrl(companyId)}/${memberId}`
    );
  }

  /**
   * Updates the sub-role of an existing team member.
   * @param companyId - Target Company id.
   * @param memberId - Team membership row id.
   * @param payload - New role value.
   */
  updateRole(
    companyId: number,
    memberId: number,
    payload: UpdateCompanyTeamMemberRolePayload
  ): Observable<CompanyTeamMember> {
    return this.http.patch<CompanyTeamMember>(
      `${this.teamMembersUrl(companyId)}/${memberId}`,
      payload
    );
  }

  private teamMembersUrl(companyId: number): string {
    return `${this.envService.getApiUrl()}/companies/${companyId}/team_members`;
  }
}
